import dayjs from 'dayjs'

export type ReportingPeriodPresetId =
  | 'current_month'
  | 'last_month'
  | 'month_before_last'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'all_time'
  | 'next_month'
  | 'next_2_months'
  | 'next_3_months'
  | 'next_6_months'
  | 'custom'

export type ReportingPeriodVariant = 'past' | 'forecast'

export interface ReportingPeriod {
  /** YYYY-MM — null means no lower bound (all time). */
  startMonth: string | null
  /** YYYY-MM — upper bound (inclusive). */
  endMonth: string
  preset: ReportingPeriodPresetId
}

export const REPORTING_PERIOD_PRESETS: ReportingPeriodPresetId[] = [
  'current_month',
  'last_month',
  'month_before_last',
  'last_3_months',
  'last_6_months',
  'last_12_months',
  'all_time',
]

export const FORECAST_PERIOD_PRESETS: ReportingPeriodPresetId[] = [
  'next_month',
  'next_2_months',
  'next_3_months',
  'next_6_months',
]

export function getDefaultReportingPeriod(): ReportingPeriod {
  return resolveReportingPreset('last_month')
}

export function getDefaultForecastPeriod(): ReportingPeriod {
  return resolveReportingPreset('next_month')
}

export function singleMonthReportingPeriod(month: string): ReportingPeriod {
  return { startMonth: month, endMonth: month, preset: 'custom' }
}

export function resolveReportingPreset(preset: ReportingPeriodPresetId): ReportingPeriod {
  const now = dayjs()

  switch (preset) {
    case 'current_month': {
      const m = now.format('YYYY-MM')
      return { startMonth: m, endMonth: m, preset }
    }
    case 'last_month': {
      const m = now.subtract(1, 'month').format('YYYY-MM')
      return { startMonth: m, endMonth: m, preset }
    }
    case 'month_before_last': {
      const m = now.subtract(2, 'month').format('YYYY-MM')
      return { startMonth: m, endMonth: m, preset }
    }
    case 'last_3_months': {
      const end = now.subtract(1, 'month')
      const start = end.subtract(2, 'month')
      return {
        startMonth: start.format('YYYY-MM'),
        endMonth: end.format('YYYY-MM'),
        preset,
      }
    }
    case 'last_6_months': {
      const end = now.subtract(1, 'month')
      const start = end.subtract(5, 'month')
      return {
        startMonth: start.format('YYYY-MM'),
        endMonth: end.format('YYYY-MM'),
        preset,
      }
    }
    case 'last_12_months': {
      const end = now.subtract(1, 'month')
      const start = end.subtract(11, 'month')
      return {
        startMonth: start.format('YYYY-MM'),
        endMonth: end.format('YYYY-MM'),
        preset,
      }
    }
    case 'all_time':
      return { startMonth: null, endMonth: now.format('YYYY-MM'), preset }
    case 'next_month': {
      const m = now.add(1, 'month').format('YYYY-MM')
      return { startMonth: m, endMonth: m, preset }
    }
    case 'next_2_months': {
      const m = now.add(2, 'month').format('YYYY-MM')
      return { startMonth: m, endMonth: m, preset }
    }
    case 'next_3_months': {
      const start = now.add(1, 'month')
      const end = start.add(2, 'month')
      return {
        startMonth: start.format('YYYY-MM'),
        endMonth: end.format('YYYY-MM'),
        preset,
      }
    }
    case 'next_6_months': {
      const start = now.add(1, 'month')
      const end = start.add(5, 'month')
      return {
        startMonth: start.format('YYYY-MM'),
        endMonth: end.format('YYYY-MM'),
        preset,
      }
    }
    default:
      return getDefaultReportingPeriod()
  }
}

export function isSingleMonthPeriod(period: ReportingPeriod): boolean {
  return period.startMonth !== null && period.startMonth === period.endMonth
}

export function isAllTimePeriod(period: ReportingPeriod): boolean {
  return period.preset === 'all_time' || period.startMonth === null
}

/** Snap calendar range selection to full calendar months (YYYY-MM). */
export function normalizeMonthRange(
  from?: Date,
  to?: Date,
  variant: ReportingPeriodVariant = 'past'
): ReportingPeriod | null {
  if (!from) return null

  let startMonth = dayjs(from).startOf('month').format('YYYY-MM')
  let endMonth = dayjs(to ?? from)
    .startOf('month')
    .format('YYYY-MM')

  const [start, end] =
    startMonth <= endMonth ? [startMonth, endMonth] : [endMonth, startMonth]

  const clamped = clampReportingPeriod(
    { startMonth: start, endMonth: end, preset: 'custom' },
    variant
  )
  return clamped
}

/** Clamp period for FNP (≤ current) or Forecast (≥ next month). */
export function clampReportingPeriod(
  period: ReportingPeriod,
  variant: ReportingPeriodVariant
): ReportingPeriod {
  const current = dayjs().format('YYYY-MM')
  const next = dayjs().add(1, 'month').format('YYYY-MM')

  if (variant === 'past') {
    let endMonth = period.endMonth > current ? current : period.endMonth
    let startMonth = period.startMonth
    if (period.preset === 'all_time' || startMonth === null) {
      return { startMonth: null, endMonth, preset: 'all_time' }
    }
    if (startMonth > endMonth) startMonth = endMonth
    return {
      startMonth,
      endMonth,
      preset: startMonth === endMonth ? (period.preset === 'custom' ? 'custom' : period.preset) : period.preset,
    }
  }

  let startMonth = period.startMonth ?? next
  let endMonth = period.endMonth
  if (startMonth < next) startMonth = next
  if (endMonth < startMonth) endMonth = startMonth
  return {
    startMonth,
    endMonth,
    preset: period.preset === 'all_time' ? 'custom' : period.preset,
  }
}

export function reportingPeriodToQueryParams(period: ReportingPeriod): Record<string, string> {
  if (isAllTimePeriod(period)) {
    return { all_time: '1', end_month: period.endMonth }
  }
  if (isSingleMonthPeriod(period)) {
    return { month: period.endMonth }
  }
  return {
    start_month: period.startMonth!,
    end_month: period.endMonth,
  }
}

export function reportingPeriodKey(period: ReportingPeriod): string {
  if (isAllTimePeriod(period)) return `all:${period.endMonth}`
  if (isSingleMonthPeriod(period)) return period.endMonth
  return `${period.startMonth}:${period.endMonth}`
}

/** Plage calendrier : 1er jour du mois de début → dernier jour du mois de fin. */
export function reportingPeriodToCalendarRange(
  period: ReportingPeriod
): { from: Date; to: Date } | undefined {
  if (period.startMonth === null) return undefined

  return {
    from: dayjs(`${period.startMonth}-01`).startOf('month').toDate(),
    to: dayjs(`${period.endMonth}-01`).endOf('month').toDate(),
  }
}

export function formatReportingPeriodLabel(
  period: ReportingPeriod,
  locale: string,
  t: (key: string) => string
): string {
  const dayjsLocale = locale.startsWith('fr') ? 'fr' : 'en'

  if (isAllTimePeriod(period)) {
    return t('reporting.periodPicker.presets.all_time')
  }

  if (isSingleMonthPeriod(period)) {
    return dayjs(`${period.endMonth}-01`).locale(dayjsLocale).format('MMMM YYYY')
  }

  const dateFormat = locale.startsWith('fr') ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
  const rangeStart = dayjs(`${period.startMonth!}-01`).startOf('month').format(dateFormat)
  const rangeEnd = dayjs(`${period.endMonth}-01`).endOf('month').format(dateFormat)

  return `${rangeStart} – ${rangeEnd}`
}

/** Future months for forecast multi-select (next N months). */
export function listFutureMonths(count = 12): string[] {
  const months: string[] = []
  let cursor = dayjs().add(1, 'month').startOf('month')
  for (let i = 0; i < count; i += 1) {
    months.push(cursor.format('YYYY-MM'))
    cursor = cursor.add(1, 'month')
  }
  return months
}
